<template>
  <div class="form-section settings-block">
    <div class="section-content">
      <AlertBanner
        v-if="alert"
        :type="alert.type"
        :title="alert.title"
        :message="alert.message"
        @dismiss="alert = null"
      />
      <div class="form-row">
        <div class="deadline-report-container">
          <div class="deadline-report-time-group">
            <label style="font-size: 12px; color: #666; margin-bottom: 4px; display: block">{{
              timeLabel
            }}</label>
            <div style="display: flex; gap: 8px; align-items: center">
              <select
                v-model="localHour"
                :disabled="disabled || saving"
                style="flex: 1; min-width: 60px"
              >
                <option :value="null">--</option>
                <option v-for="h in 24" :key="h - 1" :value="String(h - 1).padStart(2, '0')">
                  {{ String(h - 1).padStart(2, '0') }}
                </option>
              </select>
              <span>:</span>
              <select
                v-model="localMinute"
                :disabled="disabled || saving"
                style="flex: 1; min-width: 60px"
              >
                <option :value="null">--</option>
                <option v-for="m in [0, 15, 30, 45]" :key="m" :value="String(m).padStart(2, '0')">
                  {{ String(m).padStart(2, '0') }}
                </option>
              </select>
            </div>
          </div>
          <div class="deadline-report-days-group">
            <label style="font-size: 12px; color: #666; margin-bottom: 4px; display: block">{{
              daysLabel
            }}</label>
            <div style="display: flex; flex-wrap: wrap; gap: 8px">
              <label
                v-for="day in weekDays"
                :key="day.value"
                class="checkbox-label"
                style="margin: 0; font-size: 13px"
              >
                <input
                  v-model="localDays"
                  type="checkbox"
                  :value="day.value"
                  :disabled="disabled || saving || !isTimeComplete"
                />
                <span>{{ day.label }}</span>
              </label>
            </div>
          </div>
          <div v-if="showClearButton || showSaveButton" class="deadline-report-actions">
            <button
              v-if="showClearButton"
              type="button"
              :disabled="disabled || !hasReport"
              class="deadline-report-action-btn clear-deadline-report-btn"
              :class="{ active: hasReport }"
              @click="handleClear"
            >
              {{ clearLabel }}
            </button>
            <button
              v-if="showSaveButton"
              type="button"
              :disabled="disabled || saving || !hasChanges"
              class="deadline-report-action-btn btn-sm btn-primary deadline-report-save-btn"
              @click="handleSave"
            >
              {{ saving ? savingLabel : saveLabel }}
            </button>
          </div>
        </div>
        <div v-if="helpText" class="help-text">{{ helpText }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { s__, __ } from '@/locale'
import AlertBanner from '../common/AlertBanner.vue'
import { useRegularReport } from '@/composables/useRegularReport'

const props = defineProps({
  hour: {
    type: String,
    default: null,
  },
  minute: {
    type: String,
    default: null,
  },
  days: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  preferredLanguage: {
    type: String,
    default: 'en',
  },
  translationPrefix: {
    type: String,
    default: 'Profile',
  },
  showClearButton: {
    type: Boolean,
    default: true,
  },
  showSaveButton: {
    type: Boolean,
    default: true,
  },
  timeLabel: {
    type: String,
    default: () => s__('Profile|Time'),
  },
  daysLabel: {
    type: String,
    default: () => s__('Profile|Days'),
  },
  clearLabel: {
    type: String,
    default: () => __('Clear'),
  },
  saveLabel: {
    type: String,
    default: () => s__('Profile|Save'),
  },
  savingLabel: {
    type: String,
    default: () => s__('Profile|Saving...'),
  },
  helpText: {
    type: String,
    default: () =>
      s__(
        'Profile|Receive a daily deadline report at the specified time on selected days (times are in your preferred timezone)'
      ),
  },
})

const emit = defineEmits(['save', 'clear', 'update:hour', 'update:minute', 'update:days'])
const alert = ref(null)
const reportPreferredLanguage = computed(() => props.preferredLanguage || 'en')

// Use the composable for regular report logic
const {
  localHour,
  localMinute,
  localDays,
  weekDays,
  isTimeComplete,
  hasReport,
  hasChanges,
  updateOriginals,
  clear: clearReport,
  updateFromProps,
  getValues,
} = useRegularReport({
  initialHour: props.hour,
  initialMinute: props.minute,
  initialDays: props.days,
  preferredLanguage: reportPreferredLanguage,
  translationPrefix: props.translationPrefix,
})

// Watch for prop changes and update local values
watch(
  () => props.hour,
  newVal => {
    updateFromProps(newVal, props.minute, props.days)
  }
)
watch(
  () => props.minute,
  newVal => {
    updateFromProps(props.hour, newVal, props.days)
  }
)
watch(
  () => props.days,
  newVal => {
    updateFromProps(props.hour, props.minute, newVal)
  },
  { deep: true }
)

watch(localHour, newVal => {
  emit('update:hour', newVal)
})

watch(localMinute, newVal => {
  emit('update:minute', newVal)
})

watch(
  localDays,
  newVal => {
    emit('update:days', [...newVal])
  },
  { deep: true }
)

function handleClear() {
  clearReport()
  emit('clear')
}

function handleSave() {
  const values = getValues()
  emit('save', values)
  // Update original values after save is triggered (will be confirmed when parent updates props)
  updateOriginals()
}

// Expose methods for parent to set alert
defineExpose({
  setAlert: alertData => {
    alert.value = alertData
  },
  clearAlert: () => {
    alert.value = null
  },
})
</script>

<style scoped>
.deadline-report-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;
}

.deadline-report-time-group {
  display: flex;
  flex-direction: column;
}

.deadline-report-days-group {
  display: flex;
  flex-direction: column;
}

.deadline-report-days-group .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.deadline-report-days-group .checkbox-label span {
  line-height: 1.2;
}

.deadline-report-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  width: auto;
}

.deadline-report-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-height: 36px;
  box-sizing: border-box;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.2;
  border-radius: 8px;
  white-space: nowrap;
}

.clear-deadline-report-btn {
  background: #f5f5f5;
  border: 1px solid #dee2e6;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-deadline-report-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}

.clear-deadline-report-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clear-deadline-report-btn.active {
  background: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.deadline-report-save-btn {
  border-radius: 8px;
}

@media (max-width: 768px) {
  .deadline-report-actions {
    width: 100%;
    justify-content: stretch;
  }

  .deadline-report-actions > .deadline-report-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    height: 40px;
    min-height: 40px;
    justify-content: center;
    text-align: center;
  }
}
</style>
